import React from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { paymentStatusLabel } from '../payments/paymentFormat';

// backend sends local "YYYY-MM-DD HH:MM:SS" strings
export const parseDate = (value) => (value ? new Date(String(value).replace(' ', 'T')) : null);

// ticket ids are plates: an exit / close-out older than the entry is the previous visit's, not this one's
const afterEntry = (ticket, value) => {
    const entry = parseDate(ticket.entryTime);
    const t = parseDate(value);
    return t && (!entry || t > entry) ? value : null;
};
export const visitExit = (ticket) => afterEntry(ticket, ticket.exitTime);
export const visitEnd = (ticket) => visitExit(ticket) || afterEntry(ticket, ticket.closedAt);

export const formatDateTime = (value) => {
    const date = parseDate(value);
    return date && !Number.isNaN(date.getTime())
        ? date.toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' })
        : '–';
};

// entry -> exit (or close-out, or now while it is still active)
export const formatDuration = (from, to) => {
    const start = parseDate(from);
    if (!start) return '–';
    const end = parseDate(to) || new Date();
    const minutes = Math.max(0, Math.round((end - start) / 60000));
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    const mins = minutes % 60;
    if (days) return `${days} nap ${hours} óra`;
    if (hours) return `${hours} óra ${mins} perc`;
    return `${mins} perc`;
};

// "T15M" / "T1H30M" -> "15 perc" / "1 óra 30 perc"
export const formatFreeTime = (value) => {
    const m = /T(?:(\d+)H)?(?:(\d+)M)?/.exec(value || '');
    if (!m || (!m[1] && !m[2])) return value || '–';
    return [m[1] && `${+m[1]} óra`, m[2] && `${+m[2]} perc`].filter(Boolean).join(' ');
};

export const formatHuf = (amount) => `${new Intl.NumberFormat('hu-HU').format(amount || 0)} Ft`;

export const closedByLabel = (ticket) =>
    ticket.closedBy
        ? ticket.closedBy.name || ticket.closedBy.email || `#${ticket.closedBy.id}`
        : 'ismeretlen';

export const PARKING_STATES = Object.freeze({
    ALL: { label: 'Összes', params: {} },
    ACTIVE: { label: 'Aktív', params: { searchStatus: 'ACTIVE', searchClosed: 0 } },
    // the parking system closes the ticket as INVALID after the exit (EXITED in older data)
    EXITED: { label: 'Kihajtott', params: { searchStatus: 'EXITED,INVALID' } },
    CLOSED: { label: 'Kézzel lezárt', params: { searchClosed: 1 } },
});

// statuses of the parking system (getTicketInfo), seen on live data
export const PARKING_SYSTEM_STATUSES = Object.freeze({
    INVALID: {
        label: 'Kihajtott (lezárt jegy)',
        color: 'default',
        description: 'A jegyet a parkoló rendszer lezárta, az autó kihajtott.',
    },
    WRONGMODE: {
        label: 'Hibás jegy (parkoló rendszer)',
        color: 'error',
        description: 'A parkoló rendszer nem tud mit kezdeni ezzel a jeggyel / rendszámmal.',
    },
    'UNKNOWN STATUS': {
        label: 'Ismeretlen jegy (parkoló rendszer)',
        color: 'error',
        description: 'A parkoló rendszer nem ismeri ezt a jegyet / rendszámot.',
    },
});

// no real parking behind these, yet the parking system says paid=1, amount 0
const isUnknownTicket = (ticket) => ['WRONGMODE', 'UNKNOWN STATUS'].includes(ticket.status);

export function ParkingStatusChip({ ticket, size = 'small' }) {
    if (ticket.closedAt) {
        return (
            <Tooltip
                title={`${closedByLabel(ticket)}, ${formatDateTime(ticket.closedAt)}: ${
                    ticket.closeComment || ''
                }`}
            >
                <Chip size={size} color="warning" label="Kézzel lezárva" />
            </Tooltip>
        );
    }
    if (ticket.status === 'ACTIVE') {
        return <Chip size={size} color="success" variant="outlined" label="Aktív" />;
    }
    if (ticket.status === 'EXITED') {
        return <Chip size={size} label="Kihajtott" />;
    }
    // the rest comes from the parking system as is
    const known = PARKING_SYSTEM_STATUSES[ticket.status];
    return (
        <Tooltip
            title={`${
                known ? known.description : 'Ismeretlen állapot.'
            } A parkoló rendszer állapota: ${
                ticket.status || 'nincs megadva'
            } (nem az app vagy a backoffice állítja be).`}
        >
            <Chip
                size={size}
                color={known?.color || 'default'}
                variant="outlined"
                label={known ? known.label : `${ticket.status || 'Ismeretlen'} (parkoló rendszer)`}
            />
        </Tooltip>
    );
}

// after the exit the parking system sends the paid fee as a negative amount (-450 = 450 Ft was paid), the app shows 0 Ft
// compact (tables): only "450 Ft fizetve", the parking system's raw value is in the tooltip
export function TicketAmount({ amount, variant = 'body2', compact = false }) {
    if (Number(amount) >= 0) {
        return formatHuf(amount);
    }
    return (
        <Tooltip
            title={`A parkoló rendszer a lezárt jegynél negatív előjellel adja vissza a kifizetett díjat (${formatHuf(
                amount
            )}). Fizetendő már nincs, az app 0 Ft-ot mutat.`}
        >
            <Typography variant={variant} component="span" color="text.secondary">
                {formatHuf(-amount)} fizetve
                {compact ? '' : ` (parkoló rendszer: ${formatHuf(amount)})`}
            </Typography>
        </Tooltip>
    );
}

// how the current parking was paid: in the app (SimplePay), by an admin on close-out, or elsewhere (e.g. pay station)
export function PaidChip({ ticket, size = 'small' }) {
    const app = ticket.appPaymentStatus;
    if (app === 'FINISHED') {
        return (
            <Tooltip title="A vendég az appban fizetett (SimplePay)">
                <Chip size={size} color="success" label="Appban fizetve" />
            </Tooltip>
        );
    }
    if (ticket.paidByAdmin) {
        return (
            <Tooltip title="Admin ingyenesen kifizette a parkoló rendszerben (kézi lezárás)">
                <Chip size={size} color="warning" label="Admin kiengedte" />
            </Tooltip>
        );
    }
    if (isUnknownTicket(ticket)) {
        return (
            <Tooltip title="A parkoló rendszer nem ismeri a jegyet, a 'fizetve' jelzése itt nem jelent fizetést">
                <Chip size={size} variant="outlined" label="–" />
            </Tooltip>
        );
    }
    // a closed ticket says paid=0 with the paid fee as a negative amount
    if (ticket.paid || ticket.amount < 0) {
        return (
            <Tooltip
                title={
                    ticket.amount < 0
                        ? `${formatHuf(
                              -ticket.amount
                          )} fizetve a parkoló rendszerben, nem az appban (pl. parkolóautomata)`
                        : 'A parkoló rendszer szerint fizetve vagy nem kell fizetni (pl. szabad idő, bérlet), nem az appban'
                }
            >
                <Chip size={size} color="success" variant="outlined" label="Fizetve (nem appban)" />
            </Tooltip>
        );
    }
    if (app) {
        return (
            <Tooltip
                title={`Az appban indított fizetés még nem sikeres, SimplePay állapot: ${app}`}
            >
                <Chip
                    size={size}
                    color="info"
                    variant="outlined"
                    label={`Appban indítva – ${paymentStatusLabel(app)}`}
                />
            </Tooltip>
        );
    }
    return <Chip size={size} variant="outlined" label="Nem fizetve" />;
}

// one row of a parkings table (PARKINGS_TABLE_COLUMNS)
export const parkingTicketRow = (ticket) => ({
    id: ticket.id,
    // the plate / ticket number is the main identifier, make it stand out
    plate: (
        <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: 0.5 }}>
            {ticket.id}
        </Typography>
    ),
    status: <ParkingStatusChip ticket={ticket} />,
    entryTime: formatDateTime(ticket.entryTime),
    exitTime: formatDateTime(visitExit(ticket)),
    duration: formatDuration(ticket.entryTime, visitEnd(ticket)),
    amount: <TicketAmount amount={ticket.amount} compact />,
    paid: <PaidChip ticket={ticket} />,
    user: ticket.user_id ? (
        <Box>
            <Typography variant="body2">{ticket.userName || `#${ticket.user_id}`}</Typography>
            <Typography variant="caption" color="text.secondary">
                {ticket.userEmail}
            </Typography>
        </Box>
    ) : (
        <Typography variant="body2" color="text.secondary">
            –
        </Typography>
    ),
    detailsIcon: (
        <Tooltip title="Részletek">
            <IconButton size="small">
                <VisibilityIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    ),
});
